import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useWebSocket } from '../../contexts/WebSocketContext'
import { apiUrl } from '../../config/api'
import UserDisplay from '../UserDisplay/UserDisplay'
import ChessBoard from '../Chess/ChessBoard'
import './LiveGamesPreview.css'

export default function LiveGamesPreview({ onViewSpectate }) {
  const { session } = useAuth()
  const { 
    isConnected, 
    spectateGame, 
    leaveSpectate, 
    addMessageHandler, 
    removeMessageHandler 
  } = useWebSocket()
  
  const [spectableGames, setSpectableGames] = useState([])
  const [activePreviewId, setActivePreviewId] = useState(null)

  // Fetch initial list of games
  const fetchSpectableGames = useCallback(async () => {
    if (!session?.access_token) return

    try {
      const response = await fetch(apiUrl('games/spectatable?limit=3'), {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const data = await response.json()
        const games = data.games || []
        setSpectableGames(games)
        
        // If we have games, ensure we have a valid active preview
        if (games.length > 0) {
            // If no active preview, or the active preview is no longer in the list
            if (!activePreviewId || !games.find(g => g.id === activePreviewId)) {
                setActivePreviewId(games[0].id)
            }
        }
      }
    } catch (err) {
      console.error('Error fetching spectatable games:', err)
    }
  }, [session?.access_token, activePreviewId])

  // Initial fetch
  useEffect(() => {
    fetchSpectableGames()
    
    // Fallback polling to discover new games
    const interval = setInterval(fetchSpectableGames, 10000) 
    return () => clearInterval(interval)
  }, [fetchSpectableGames])


  // Manage WebSocket subscription for the active preview
  useEffect(() => {
    if (!isConnected || !activePreviewId) return

    // Subscribe to the game
    console.log(`LiveGames: Subscribing to ${activePreviewId}`)
    spectateGame(activePreviewId).catch(err => {
        console.error("Failed to subscribe to preview game:", err)
    })

    // Handler for move updates
    const handleMoveUpdate = (message) => {
        if (message.game_id === activePreviewId) {
            setSpectableGames(prevGames => {
                return prevGames.map(g => {
                    if (g.id === activePreviewId) {
                        return { 
                            ...g, 
                            board: message.current_board, // Top-level property
                            last_move: message.move, // Top-level property
                            current_turn: message.current_turn,
                            white_time: message.white_time_remaining,
                            black_time: message.black_time_remaining,
                            // Update special squares and effects
                            red_squares: message.red_squares,
                            blue_squares: message.blue_squares,
                            green_squares: message.green_squares,
                            visual_effects: message.visual_effects
                        }
                    }
                    return g
                })
            })
        }
    }

    // Handler for full game state updates
    const handleGameState = (message) => {
         if (message.game_id === activePreviewId) {
             const gameData = message.data?.game || message.data
             if (gameData) {
                setSpectableGames(prevGames => {
                    return prevGames.map(g => {
                        if (g.id === activePreviewId) {
                            return gameData // Full update
                        }
                        return g
                    })
                })
             }
         }
    }

    addMessageHandler('move_update', handleMoveUpdate)
    addMessageHandler('game_state', handleGameState)

    return () => {
        console.log(`LiveGames: Unsubscribing from ${activePreviewId}`)
        leaveSpectate() // This sends the leave_spectate message
        removeMessageHandler('move_update')
        removeMessageHandler('game_state')
    }
  }, [isConnected, activePreviewId, spectateGame, leaveSpectate, addMessageHandler, removeMessageHandler])


  const game = spectableGames.find(g => g.id === activePreviewId) || spectableGames[0]

  return (
    <div className="game-card spectate-game">
      <div className="spectate-header">
        <div className="spectate-title-section">
            <h4>Live Games</h4>
            {spectableGames.length === 0 ? (
                <p>No live games available</p>
            ) : (
                <p style={{display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap'}}>
                    <UserDisplay user={game.white_player} showTitleBadges={false} showStatusBadges={true} />
                    <span>vs</span>
                    <UserDisplay user={game.black_player} showTitleBadges={false} showStatusBadges={true} />
                </p>
            )}
        </div>
        <button
            className="view-all-button"
            onClick={onViewSpectate}
        >
            View All
        </button>
      </div>

      {spectableGames.length === 0 ? (
        <div className="no-games-placeholder">
            <div className="placeholder-text">No live games available</div>
        </div>
      ) : (
        <div className="full-board-preview">
            <div className="full-chess-board-container">
            <ChessBoard
                game={{
                ...game,
                board: game.current_board || game.board
                }}
                onSquareClick={() => {}} 
                selectedSquare={null}
                legalMoves={[]}
                lastMove={game.last_move}
                isPlayerWhite={true} 
                disabled={true} 
                allowResize={false}
            />
            </div>

            <div className="board-click-overlay" onClick={onViewSpectate}>
            <div className="overlay-text">Click to Spectate</div>
            </div>
        </div>
      )}
    </div>
  )
}
